/**
 * ModulePickerContent - Shared module list content used by both:
 * - ModuleSwitcher (header popover)
 * - Sidebar (inline dropdown)
 *
 * At rest, shows "Your Modules" (favorites + used by frequency) and
 * "Other Modules" (never used, dimmed). When searching, module matches collapse
 * into one "Modules" heading (mirroring the "Pages" heading) and every available
 * module renders full-strength — the never-used dimming is suppressed so an
 * available match isn't mistaken for an unavailable one and missed. Search also
 * matches page names within modules.
 */

import React, { useRef, useEffect, useMemo, useState } from 'react';
import { Search, Star, ChevronRight, Home } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { Separator } from '../ui/separator';
import { ModuleConfig, NavItem } from '../../shared/config/modules.config';
import { useModuleName } from '../../shared/hooks/useModuleName';
import { ModuleStatusIndicator } from '../../shared/components/ModuleStatusIndicator';
import { searchScore } from '../../shared/utils/searchScore';
import {
  createEnglishTranslator,
  englishAliases,
  matchModules,
  matchModulePages,
  type ModulePageMatch,
} from '../../shared/utils/moduleSearch';
import { useNavItemAccess } from '../../shared/hooks/useNavItemAccess';
import { isNavItemVisible } from '../../shared/utils/navItemAccess';
import { BidiText } from '../../shared/components/common';
import { NAV_ITEM_SELECTED_BG } from '../../shared/styles/sidebar';
import { cn } from '../../lib/utils';

/** Dynamic sidebar items (agents, workspaces, boards) searchable in the picker */
export interface DynamicPickerItem {
  /** Display name */
  label: string;
  /**
   * The `common` i18n key `label` was translated from, when it HAS one.
   *
   * Entity-tier rows are named from the config (`entities.*.pluralKey`), so they
   * are display strings in the user's language exactly like a nav page label —
   * and, like one, they must stay findable by their English name whatever the UI
   * language (see `moduleSearch`'s `TranslateEnglishKey`). Rows whose label is
   * user data — an agent, workspace or board someone named — have no key and
   * correctly get no English alias.
   */
  labelKey?: string;
  /** Navigation path */
  path: string;
  /** Module ID this item belongs to */
  moduleId: string;
  /** Category for grouping (e.g., "Agents", "Workspaces", "Boards") */
  category: string;
}

interface DynamicMatch {
  item: DynamicPickerItem;
  module: ModuleConfig;
  /** Relevance score (higher first) — see {@link searchScore}. */
  score: number;
}

/** A single keyboard-navigable row in the flattened picker list. */
interface FlatRow {
  /** Stable key matching the rendered row's key. */
  key: string;
  /** Whether the row can be activated (disabled rows are skipped by arrows). */
  disabled: boolean;
  /** Navigates to the row's target. */
  activate: () => void;
}

interface ModulePickerContentProps {
  /** All enabled modules */
  pickerModules: ModuleConfig[];
  /** All disabled modules */
  disabledPickerModules: ModuleConfig[];
  /** Current search query */
  searchQuery: string;
  /** Search query setter */
  onSearchChange: (query: string) => void;
  /** Whether a module is the current one */
  isCurrentModule: (id: string) => boolean;
  /** Whether a module is enabled for the user */
  isModuleEnabledForUser: (id: string) => boolean;
  /** Module select handler */
  onModuleSelect: (module: ModuleConfig) => void;
  /** Page select handler — navigates directly to a page path */
  onPageSelect?: (path: string, module: ModuleConfig) => void;
  /** Home select handler — navigates to global home */
  onHomeSelect?: () => void;
  /** Whether a module is favorited */
  isFavorite: (id: string) => boolean;
  /** Toggle favorite handler */
  onToggleFavorite: (moduleId: string, e: React.MouseEvent) => void;
  /** Sort modules into yourModules/otherModules groups */
  sortModules: <T extends { id: string }>(modules: T[], getName: (m: T) => string) => { yourModules: T[]; otherModules: T[] };
  /** Whether the picker is open (for auto-focus) */
  isOpen: boolean;
  /** Max height for the module list */
  maxHeight?: string;
  /** Utility modules pinned at the bottom of the picker */
  pinnedModules?: ModuleConfig[];
  /** Dynamic sidebar items (agents, workspaces, boards) searchable in picker */
  dynamicItems?: DynamicPickerItem[];
  /**
   * Suppress navItem "page" matches. Callers that surface their own
   * second-tier rows via `dynamicItems` (e.g. the Data Import target picker,
   * which lists importable entities) set this so the nav-page results don't
   * appear alongside — they'd be selectable look-alikes that only set the
   * module and discard the entity. Default false (page matches shown).
   */
  suppressPageMatches?: boolean;
}

function renderModuleIcon(module: ModuleConfig, dimmed = false) {
  const ModuleIcon = module.icon as React.ElementType;
  return (
    <div
      className={cn(
        'w-6 h-6 rounded flex items-center justify-center shrink-0',
        dimmed && 'opacity-50'
      )}
      style={{ backgroundColor: `${module.color}${dimmed ? '15' : '20'}` }}
    >
      {typeof ModuleIcon === 'function' ? (
        <ModuleIcon className="h-3.5 w-3.5" style={{ color: module.color }} />
      ) : (
        <span className="[&>svg]:h-3.5 [&>svg]:w-3.5" style={{ color: module.color }}>
          {module.icon}
        </span>
      )}
    </div>
  );
}

/**
 * A page result's icon.
 *
 * `NavItem.icon` is a rendered ELEMENT, not a component — `modules.config.tsx`
 * builds it as `getIcon(item.icon) ?? <></>`, and `getIcon` returns
 * `ICON_MAP[name]`, which is `<Foo className="h-4 w-4" />`. So the
 * `typeof NavIcon === 'function'` arm is never the one taken for a config-driven
 * item, and without the element fallback below EVERY page result rendered an
 * empty icon square. `renderModuleIcon` directly above has always carried that
 * fallback; this one was missing it, so the defect was invisible unless you
 * compared a module row with a page row side by side.
 *
 * The `[&>svg]:` selector re-sizes the element's own svg, exactly as the module
 * row does — the element arrives fixed at `h-4 w-4` and the picker wants 3.5.
 */
function renderNavItemIcon(navItem: NavItem, module: ModuleConfig) {
  const NavIcon = navItem.icon as React.ElementType;
  if (typeof NavIcon === 'function') {
    return <NavIcon className="h-3.5 w-3.5" style={{ color: module.color }} />;
  }
  if (!navItem.icon) return null;
  return (
    <span className="[&>svg]:h-3.5 [&>svg]:w-3.5" style={{ color: module.color }}>
      {navItem.icon}
    </span>
  );
}

/**
 * Background/hover classes for a picker row. The current row reuses the
 * sidebar's selected-nav-item tint — the app's ambient module accent at low
 * alpha — rather than a grey fill, so "the one I'm on" reads the same in the
 * picker as it does in the sidebar. The keyboard-active row gets a solid fill
 * plus a primary-colored inset ring so it stays clearly visible even against
 * the current-module tint and the lighter hover state.
 *
 * A current row also positions the marker below, so it asks for `relative`;
 * the other row types (page matches, dynamic items) don't need it.
 */
function rowStateClasses(isCurrent: boolean, isActive: boolean): string {
  return cn(
    isCurrent
      ? cn('relative', NAV_ITEM_SELECTED_BG)
      : isActive
        ? 'bg-accent'
        : 'hover:bg-accent/50',
    isActive && 'ring-2 ring-inset ring-primary'
  );
}

/**
 * The current-module marker: a leading bar in the ambient module accent —
 * solid, like the active-tab underline it echoes, because 2px of a 15% wash
 * would not read at all. Paired with a semibold label so the state is carried
 * by more than colour alone (WCAG 1.4.1).
 *
 * It sits in the row's padding rather than the content flow, which is what
 * frees the trailing slot for the favorite star — the check icon that used to
 * live there made the current module's favorite toggle unreachable.
 */
/**
 * The favorite star's coarse-pointer treatment.
 *
 * At rest on a fine pointer the star is hidden and revealed on row hover, which
 * keeps the list quiet. A coarse pointer never hovers, and `opacity: 0` does NOT
 * take an element out of the hit path — so that alone left the trailing edge of
 * every row as an invisible control that silently (un)favorited instead of
 * switching module (each consumer's toggle handler stops propagation). The
 * control is made honest on touch: visible, and a real 44px box.
 *
 * The 44px comes from the button's OWN layout box (`touch:h-11 touch:w-11`),
 * not from the `::after` overlay that `Switch` and `Checkbox` use. Those live in
 * containers that can absorb an all-round margin; a picker row is ~36px tall, so
 * an overlay large enough to reach 44px would spill past the row and a tap near
 * a boundary would favorite the neighbouring module — the exact failure this
 * item is fixing, moved one row over. Growing the box instead simply makes the
 * row taller on touch, where the extra height is wanted anyway.
 *
 * The icon steps up to `h-4 w-4` on touch: at 12px the star is a smudge on a
 * phone, and it is now a permanently visible control rather than a hover
 * flourish.
 */
const FAVORITE_TOUCH_TARGET =
  'touch:h-11 touch:w-11 touch:p-0 touch:flex touch:items-center touch:justify-center touch:opacity-100';

function CurrentModuleMarker() {
  return (
    <span
      aria-hidden="true"
      className="absolute inset-y-1 start-0 w-0.5 rounded-full bg-module-accent"
    />
  );
}

export function ModulePickerContent({
  pickerModules,
  disabledPickerModules,
  searchQuery,
  onSearchChange,
  isCurrentModule,
  isModuleEnabledForUser,
  onModuleSelect,
  onPageSelect,
  onHomeSelect,
  isFavorite,
  onToggleFavorite,
  sortModules,
  isOpen,
  maxHeight = '400px',
  pinnedModules = [],
  dynamicItems = [],
  suppressPageMatches = false,
}: ModulePickerContentProps) {
  const { t, i18n } = useTranslation('common');
  // English module/page names stay searchable whatever the UI language — the
  // product's canonical vocabulary is English and users type it regardless.
  const translateEnglish = useMemo(() => createEnglishTranslator(i18n), [i18n]);
  const getModuleName = useModuleName();
  // Per-nav-item gate — the same one the sidebar and global search apply, so a
  // page match here can never offer a page the sidebar hides.
  const navItemAccess = useNavItemAccess();
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Focus search when picker opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Split and sort modules using shared sorting logic
  const { yourModules, otherModules } = useMemo(() => {
    const allModules = [...pickerModules, ...disabledPickerModules];

    if (!searchQuery) {
      return sortModules(allModules, (m) => getModuleName(m));
    }

    // Matching (name + synonyms, exact-over-fuzzy) is the shared navigation
    // search engine — global search ranks modules identically.
    const { modules: filtered, scoreById } = matchModules(
      allModules,
      searchQuery,
      getModuleName,
      translateEnglish,
    );

    const result = sortModules(filtered, (m) => getModuleName(m));

    // Sort each group by search relevance (higher score first).
    const byScore = (a: ModuleConfig, b: ModuleConfig) =>
      (scoreById.get(b.id) ?? 0) - (scoreById.get(a.id) ?? 0);
    result.yourModules.sort(byScore);
    result.otherModules.sort(byScore);

    return result;
  }, [searchQuery, pickerModules, disabledPickerModules, sortModules, getModuleName, translateEnglish]);

  // Find page matches when searching — the same shared engine, and therefore the
  // same ranking, as global search. A page whose module ALSO matched still
  // surfaces when its own label matches; only the page the module row itself
  // opens (`module.defaultPath`) is dropped as a duplicate of that row.
  const pageMatches = useMemo<ModulePageMatch[]>(() => {
    if (suppressPageMatches) return [];

    const matchedModuleIds = new Set([...yourModules, ...otherModules].map(m => m.id));
    return matchModulePages(
      [...pickerModules, ...disabledPickerModules],
      searchQuery,
      matchedModuleIds,
      getModuleName,
      t,
      translateEnglish,
      (navItem) => isNavItemVisible(navItem, navItemAccess),
    );
  }, [searchQuery, yourModules, otherModules, pickerModules, disabledPickerModules, suppressPageMatches, getModuleName, t, translateEnglish, navItemAccess]);

  // Find dynamic item matches (agents, workspaces, boards)
  const dynamicMatches = useMemo<DynamicMatch[]>(() => {
    if (!searchQuery || searchQuery.length < 2 || dynamicItems.length === 0) return [];

    const allModules = [...pickerModules, ...disabledPickerModules];
    const moduleMap = new Map(allModules.map(m => [m.id, m]));

    return dynamicItems
      .map(item => {
        const module = moduleMap.get(item.moduleId);
        if (!module) return null;
        // A config-named row (the entity tier) folds its English name in as an
        // alias, exactly as a nav page label does — so "contacts" finds the
        // Contacts entity in a Hebrew session. A user-named row has no key and
        // is matched on its own name alone.
        const aliases = item.labelKey
          ? englishAliases(translateEnglish(item.labelKey), item.label)
          : [];
        const score = searchScore(item.label, searchQuery, aliases);
        if (score <= 0) return null;
        return { item, module, score };
      })
      .filter((m): m is DynamicMatch => m !== null)
      .sort((a, b) => b.score - a.score);
  }, [searchQuery, dynamicItems, pickerModules, disabledPickerModules, translateEnglish]);

  const hasModuleResults = yourModules.length > 0 || otherModules.length > 0;
  const hasPageResults = pageMatches.length > 0;
  const hasDynamicResults = dynamicMatches.length > 0;
  const hasAnyResults = hasModuleResults || hasPageResults || hasDynamicResults;
  // The pinned section renders as a separate listbox below the scroll area.
  const pinnedListboxRendered = pinnedModules.length > 0 && !searchQuery;

  // --- Keyboard navigation -------------------------------------------------
  // Flatten every selectable row into a single ordered list so arrow keys can
  // walk through modules, page matches, dynamic items and pinned modules as
  // one continuous sequence (the order matches the rendered DOM order).
  const flatRows = useMemo<FlatRow[]>(() => {
    const rows: FlatRow[] = [];
    const pushModule = (module: ModuleConfig, prefix: string) => {
      const disabled = !isModuleEnabledForUser(module.id);
      rows.push({
        key: `${prefix}-${module.id}`,
        disabled,
        activate: () => {
          if (!disabled) onModuleSelect(module);
        },
      });
    };
    yourModules.forEach((m) => pushModule(m, 'mod'));
    otherModules.forEach((m) => pushModule(m, 'mod'));
    pageMatches.forEach(({ module, navItem }) => {
      const disabled = !isModuleEnabledForUser(module.id);
      rows.push({
        key: `page-${module.id}-${navItem.path}`,
        disabled,
        activate: () => {
          if (disabled) return;
          if (onPageSelect) onPageSelect(navItem.path, module);
          else onModuleSelect(module);
        },
      });
    });
    dynamicMatches.forEach(({ item, module }) => {
      const disabled = !isModuleEnabledForUser(module.id);
      rows.push({
        key: `dyn-${item.path}`,
        disabled,
        activate: () => {
          if (disabled) return;
          if (onPageSelect) onPageSelect(item.path, module);
          else onModuleSelect(module);
        },
      });
    });
    // Pinned modules only render when not searching.
    if (!searchQuery) {
      pinnedModules.forEach((module) => {
        rows.push({
          key: `pin-${module.id}`,
          disabled: false,
          activate: () => onModuleSelect(module),
        });
      });
    }
    return rows;
  }, [
    yourModules,
    otherModules,
    pageMatches,
    dynamicMatches,
    pinnedModules,
    searchQuery,
    isModuleEnabledForUser,
    onModuleSelect,
    onPageSelect,
  ]);

  const keyToIndex = useMemo(() => {
    const map = new Map<string, number>();
    flatRows.forEach((row, i) => map.set(row.key, i));
    return map;
  }, [flatRows]);

  // -1 means "no row highlighted". When searching we pre-highlight the first
  // result so Enter opens the top match without an arrow press first.
  const [activeIndex, setActiveIndex] = useState(-1);
  const listRef = useRef<HTMLDivElement>(null);

  // Latest flatRows for use inside effects without making them a dependency —
  // flatRows changes reference on unrelated re-renders, which must not reset
  // the user's keyboard highlight.
  const flatRowsRef = useRef(flatRows);
  flatRowsRef.current = flatRows;

  // Re-highlight the top result when the query changes; clear the highlight
  // when the picker closes or the query is emptied.
  useEffect(() => {
    if (!isOpen || !searchQuery) {
      setActiveIndex(-1);
      return;
    }
    setActiveIndex(flatRowsRef.current.findIndex((r) => !r.disabled));
  }, [searchQuery, isOpen]);

  // Keep the highlighted row scrolled into view.
  useEffect(() => {
    if (activeIndex < 0) return;
    const el = listRef.current?.querySelector<HTMLElement>(
      `[data-picker-index="${activeIndex}"]`
    );
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Enter') return;
    const enabled = flatRows.reduce<number[]>((acc, row, i) => {
      if (!row.disabled) acc.push(i);
      return acc;
    }, []);
    if (enabled.length === 0) return;

    if (e.key === 'Enter') {
      if (activeIndex >= 0 && activeIndex < flatRows.length && !flatRows[activeIndex].disabled) {
        e.preventDefault();
        flatRows[activeIndex].activate();
      }
      return;
    }

    e.preventDefault();
    const dir = e.key === 'ArrowDown' ? 1 : -1;
    const pos = enabled.indexOf(activeIndex);
    const nextPos =
      pos === -1
        ? dir === 1
          ? 0
          : enabled.length - 1
        : Math.min(Math.max(pos + dir, 0), enabled.length - 1);
    setActiveIndex(enabled[nextPos]);
  };

  /**
   * Render a single module row. `muted` only dims a module that the user simply
   * never *used* (the resting "Other modules" grouping). It is NEVER set while
   * searching — an available module in a search match renders full-strength so
   * it isn't mistaken for an unavailable one and missed. A module the user has
   * no access to (`!isModuleEnabledForUser`) is still dimmed via `opacity-40`.
   */
  const renderModuleRow = (module: ModuleConfig, muted: boolean) => {
    const isCurrent = isCurrentModule(module.id);
    const isDisabled = !isModuleEnabledForUser(module.id);
    const isFav = isFavorite(module.id);
    const rowIndex = keyToIndex.get(`mod-${module.id}`) ?? -1;
    const isActive = rowIndex === activeIndex;

    return (
      <div
        key={module.id}
        id={`module-picker-row-${rowIndex}`}
        data-picker-index={rowIndex}
        role="option"
        aria-selected={isActive}
        aria-current={isCurrent ? 'true' : undefined}
        title={t(module.descriptionKey)}
        className={cn(
          'flex items-center gap-2 px-2 py-1.5 transition-colors group cursor-pointer',
          rowStateClasses(isCurrent, isActive),
          isDisabled && 'opacity-40 cursor-not-allowed'
        )}
        onClick={() => !isDisabled && onModuleSelect(module)}
      >
        {isCurrent && <CurrentModuleMarker />}
        <ModuleStatusIndicator status={module.status} category={module.category} size="sm">
          {renderModuleIcon(module, muted)}
        </ModuleStatusIndicator>
        {/* A module name is a TRANSLATED label, so its language is the document's by
            construction — it follows the document direction, and must NOT be a
            `dir="auto"` BidiText. `auto` bases direction on the first strong character,
            so a Hebrew translation opening with a Latin acronym ("CRM צינור מכירות")
            flips this one row to LTR while the sidebar header beside it stays RTL.
            BidiText is for user-supplied names of unknown script (workspace, board). */}
        <span
          className={cn(
            'truncate flex-1 text-sm text-start',
            isCurrent && 'font-semibold',
            muted && 'text-muted-foreground'
          )}
        >
          {getModuleName(module)}
        </span>
        {!isDisabled && (
          <button
            onClick={(e) => onToggleFavorite(module.id, e)}
            aria-label={isFav ? t('modules.removeFromFavorites') : t('modules.addToFavorites')}
            aria-pressed={isFav}
            className={cn(
              'p-0.5 rounded transition-all shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              FAVORITE_TOUCH_TARGET,
              isFav
                ? 'text-amber-500 hover:text-amber-600'
                : 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100 text-muted-foreground/40 hover:text-amber-500'
            )}
          >
            <Star className={cn('h-3 w-3 touch:h-4 touch:w-4', isFav && 'fill-current')} />
          </button>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Home button + Search input */}
      <div className="p-2 border-b flex items-center gap-1.5">
        {onHomeSelect && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={onHomeSelect}
                aria-label={t('nav.home')}
                className="shrink-0 w-8 h-8 touch:min-h-[44px] touch:min-w-[44px] rounded-md flex items-center justify-center hover:bg-accent transition-colors"
              >
                <Home className="h-4 w-4 text-muted-foreground" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {t('nav.home')}
            </TooltipContent>
          </Tooltip>
        )}
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <input
            ref={searchInputRef}
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder={t('modules.searchModules')}
            role="combobox"
            data-testid="module-picker-search"
            aria-expanded={isOpen}
            aria-controls={
              pinnedListboxRendered
                ? 'module-picker-listbox module-picker-pinned-listbox'
                : 'module-picker-listbox'
            }
            aria-activedescendant={
              activeIndex >= 0 ? `module-picker-row-${activeIndex}` : undefined
            }
            className="w-full pl-8 pr-3 py-1.5 text-sm bg-transparent border rounded-md focus:outline-none focus:ring-1 focus:ring-ring"
          />
        </div>
      </div>

      {/* Module list */}
      <div
        ref={listRef}
        id="module-picker-listbox"
        role="listbox"
        // `min-h-0 flex-1` is what lets `maxHeight` behave as a design CEILING
        // rather than a pin: the two popover hosts below cap themselves to the
        // space Radix measured, and this list is the part that absorbs the
        // shortfall — the search input above and the pinned section below keep
        // their size. Inert in the Sidebar's mobile host, which is not a flex
        // column.
        className="min-h-0 flex-1 overflow-y-auto py-1"
        style={{ maxHeight }}
      >
        {!hasAnyResults && (
          <div className="px-3 py-6 text-center text-sm text-muted-foreground">
            {t('modules.noResultsFound')}
          </div>
        )}

        {/* MODULES section (searching) — one "Modules" heading mirroring the
            "Pages" heading so module matches read as clearly as page matches.
            Available modules render full-strength (never dimmed-as-unused). */}
        {searchQuery && hasModuleResults && (
          <>
            <span className="text-[10px] text-muted-foreground uppercase tracking-wider px-3 pt-1 pb-0.5 block">
              {t('modules.title')}
            </span>
            {[...yourModules, ...otherModules].map((module) => renderModuleRow(module, false))}
          </>
        )}

        {/* YOUR MODULES section (resting state) */}
        {!searchQuery && yourModules.length > 0 && (
          <span className="text-[10px] text-muted-foreground uppercase tracking-wider px-3 pt-1 pb-0.5 block">
            {t('modules.yourModules')}
          </span>
        )}
        {!searchQuery && yourModules.map((module) => renderModuleRow(module, false))}

        {/* OTHER MODULES section (resting state) — dimmed as never-used */}
        {!searchQuery && otherModules.length > 0 && (
          <>
            <div className="px-3 pt-2 pb-0.5">
              <Separator className="mb-1.5" />
              <span className="text-[10px] text-muted-foreground uppercase tracking-wider block">
                {t('modules.otherModules')}
              </span>
            </div>
            {otherModules.map((module) => renderModuleRow(module, true))}
          </>
        )}

        {/* PAGE MATCHES section — shown when search matches navItem labels */}
        {hasPageResults && (
          <>
            {hasModuleResults && (
              <div className="px-3 pt-2 pb-0.5">
                <Separator className="mb-1.5" />
              </div>
            )}
            <span className="text-[10px] text-muted-foreground uppercase tracking-wider px-3 pt-1 pb-0.5 block">
              {t('modules.pages')}
            </span>
            {pageMatches.map(({ module, navItem }) => {
              const isDisabled = !isModuleEnabledForUser(module.id);
              const rowIndex = keyToIndex.get(`page-${module.id}-${navItem.path}`) ?? -1;
              const isActive = rowIndex === activeIndex;

              return (
                <div
                  key={`${module.id}-${navItem.path}`}
                  id={`module-picker-row-${rowIndex}`}
                  data-picker-index={rowIndex}
                  role="option"
                  aria-selected={isActive}
                  title={navItem.descriptionKey ? t(navItem.descriptionKey) : undefined}
                  className={cn(
                    'flex items-center gap-2 px-2 py-1.5 transition-colors cursor-pointer',
                    rowStateClasses(false, isActive),
                    isDisabled && 'opacity-40 cursor-not-allowed'
                  )}
                  onClick={() => {
                    if (isDisabled) return;
                    if (onPageSelect) {
                      onPageSelect(navItem.path, module);
                    } else {
                      onModuleSelect(module);
                    }
                  }}
                >
                  <div
                    className="w-6 h-6 rounded flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${module.color}20` }}
                  >
                    {renderNavItemIcon(navItem, module)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <BidiText className="truncate text-sm block">{t(navItem.labelKey)}</BidiText>
                    <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                      {getModuleName(module)}
                      <ChevronRight className="h-3 w-3 rtl:rotate-180" />
                      {t(navItem.labelKey)}
                    </span>
                  </div>
                </div>
              );
            })}
          </>
        )}
        {/* DYNAMIC ITEM MATCHES — agents, workspaces, boards */}
        {hasDynamicResults && (
          <>
            {(hasModuleResults || hasPageResults) && (
              <div className="px-3 pt-2 pb-0.5">
                <Separator className="mb-1.5" />
              </div>
            )}
            {/* Group by category */}
            {Array.from(new Set(dynamicMatches.map(m => m.item.category))).map(category => (
              <React.Fragment key={category}>
                <span className="text-[10px] text-muted-foreground uppercase tracking-wider px-3 pt-1 pb-0.5 block">
                  {category}
                </span>
                {dynamicMatches
                  .filter(m => m.item.category === category)
                  .map(({ item, module }) => {
                    const isDisabled = !isModuleEnabledForUser(module.id);
                    const rowIndex = keyToIndex.get(`dyn-${item.path}`) ?? -1;
                    const isActive = rowIndex === activeIndex;

                    return (
                      <div
                        key={item.path}
                        id={`module-picker-row-${rowIndex}`}
                        data-picker-index={rowIndex}
                        role="option"
                        aria-selected={isActive}
                        className={cn(
                          'flex items-center gap-2 px-2 py-1.5 transition-colors cursor-pointer',
                          rowStateClasses(false, isActive),
                          isDisabled && 'opacity-40 cursor-not-allowed'
                        )}
                        onClick={() => {
                          if (isDisabled) return;
                          if (onPageSelect) {
                            onPageSelect(item.path, module);
                          } else {
                            onModuleSelect(module);
                          }
                        }}
                      >
                        {renderModuleIcon(module)}
                        <div className="flex-1 min-w-0">
                          {/* `item.label` IS user data (an agent/workspace/board someone
                              named), so it keeps its auto-detected direction. The module
                              name under it is a translated label and must not — see the
                              note on the module row above. */}
                          <BidiText className="truncate text-sm block">{item.label}</BidiText>
                          <span className="text-[11px] text-muted-foreground block truncate">
                            {getModuleName(module)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
              </React.Fragment>
            ))}
          </>
        )}
      </div>

      {/* PINNED MODULES section - always visible at bottom, outside scroll */}
      {pinnedListboxRendered && (
        <div className="border-t shrink-0">
          <div className="py-1" id="module-picker-pinned-listbox" role="listbox">
            {pinnedModules.map((module) => {
              const isCurrent = isCurrentModule(module.id);
              const rowIndex = keyToIndex.get(`pin-${module.id}`) ?? -1;
              const isActive = rowIndex === activeIndex;

              return (
                <div
                  key={module.id}
                  id={`module-picker-row-${rowIndex}`}
                  data-picker-index={rowIndex}
                  role="option"
                  aria-selected={isActive}
                  aria-current={isCurrent ? 'true' : undefined}
                  title={t(module.descriptionKey)}
                  className={cn(
                    'flex items-center gap-2 px-2 py-1.5 transition-colors cursor-pointer',
                    rowStateClasses(isCurrent, isActive)
                  )}
                  onClick={() => onModuleSelect(module)}
                >
                  {isCurrent && <CurrentModuleMarker />}
                  {renderModuleIcon(module)}
                  <span className={cn('truncate flex-1 text-sm text-start', isCurrent && 'font-semibold')}>
                    {getModuleName(module)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}
